import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { systemUpdatesApi, SystemUpdate } from '../domains/admin/api/systemUpdates';
import { useAuth } from '../contexts/AuthContext';

/**
 * One-time "what's new" toast shown on the first page after login when
 * there is at least one unseen release_note in system_updates. Clicking
 * "See what changed" deep-links to /changelog and marks the release as
 * dismissed so the toast never re-appears for that user.
 *
 * Intentionally separate from the full login-banner flow and the AI chat
 * delivery — this is the lightweight nudge that lives in the corner and
 * does not block anything.
 */
export default function ReleaseToast() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState<SystemUpdate | null>(null);
  const [hiding, setHiding] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    const timer = setTimeout(async () => {
      try {
        const res = await systemUpdatesApi.getUnseenUpdates();
        const first = (res.updates || []).find(u => u.show_on_login && !u.dismissed);
        if (first) setPending(first);
      } catch {
        // silent — toast is best-effort
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, [isAuthenticated, user?.id]);

  if (!pending) return null;

  const dismiss = async () => {
    setHiding(true);
    try { await systemUpdatesApi.dismissUpdate(pending.id); } catch { /* noop */ }
    setTimeout(() => setPending(null), 200);
  };

  const openChangelog = async () => {
    try { await systemUpdatesApi.dismissUpdate(pending.id); } catch { /* noop */ }
    navigate(`/changelog?v=${pending.id}`);
    setPending(null);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className={`fixed bottom-6 right-6 z-[9999] max-w-sm transition-all duration-200 ${hiding ? 'opacity-0 translate-y-2' : 'opacity-100'}`}
    >
      <div className="rounded-lg border border-slate-200 bg-white shadow-xl overflow-hidden">
        <div className="bg-slate-900 px-4 py-2">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-300">What's new on EIS-Tech</p>
        </div>
        <div className="p-4">
          <h3 className="text-sm font-semibold text-slate-900 line-clamp-2">{pending.title}</h3>
          {pending.summary && (
            <p className="mt-1 text-sm text-slate-600 line-clamp-3">{pending.summary}</p>
          )}
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={openChangelog}
              className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700"
            >
              See what changed
            </button>
            <button
              onClick={dismiss}
              className="rounded px-3 py-1.5 text-sm font-medium text-slate-500 hover:text-slate-700"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
